import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'

import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { ApiResponse } from '@/types/api'
import type { PostAdmitData, PostAdmitTaskKey } from '@/types/models'
import type { ProgramTemplate } from '@/types/models'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TASK_ORDER: PostAdmitTaskKey[] = [
  'decisionAndOfferManagement',
  'financialAidNegotiation',
  'admissionFeePayment',
  'getVisaDocuments',
  'figureFundingAndLoans',
  'healthCheckAndMedical',
  'applyForVisa',
  'visaInterview',
  'fixTravelPlans',
  'findAccommodation',
  'getCellularConnection',
  'setupBankingAndCredit',
]

const TASK_DISPLAY_NAMES: Record<PostAdmitTaskKey, string> = {
  decisionAndOfferManagement: 'Decision and Offer Management',
  financialAidNegotiation: 'Financial Aid Negotiation',
  admissionFeePayment: 'Admission Fee Payment',
  getVisaDocuments: 'Get Visa Documents',
  figureFundingAndLoans: 'Figure Funding and Loans',
  healthCheckAndMedical: 'Health Check and Medical',
  applyForVisa: 'Apply for Visa',
  visaInterview: 'Visa Interview',
  fixTravelPlans: 'Fix Travel Plans',
  findAccommodation: 'Find Accommodation',
  getCellularConnection: 'Get Cellular Connection',
  setupBankingAndCredit: 'Setup Banking and Credit',
}

const TASK_DEFAULT_DESCRIPTIONS: Record<PostAdmitTaskKey, string> = {
  decisionAndOfferManagement: 'Review all offer letters and compare programs. Confirm your enrollment decision before the deadline.',
  financialAidNegotiation: 'Reach out to the financial aid office to negotiate scholarships or fellowships if applicable.',
  admissionFeePayment: 'Pay the non-refundable enrollment deposit to secure your seat in the program.',
  getVisaDocuments: 'Gather all required documents for your student visa application including I-20/CAS letter and financial proofs.',
  figureFundingAndLoans: 'Finalize your funding plan — education loans, employer sponsorship, or personal savings.',
  healthCheckAndMedical: 'Complete any required health forms and vaccinations for enrollment. Register with student health services.',
  applyForVisa: 'Submit your student visa application via the official embassy website or VFS.',
  visaInterview: 'Attend your visa interview at the embassy. Bring all required documents.',
  fixTravelPlans: 'Book your flights and make travel arrangements. Check baggage allowances for international moves.',
  findAccommodation: 'Secure on-campus or off-campus housing. Connect with the university housing office.',
  getCellularConnection: 'Research local SIM card options or international plans for your destination country.',
  setupBankingAndCredit: 'Open a local bank account and set up a credit card for your destination country.',
}

const COUNTRIES = [
  'India', 'USA', 'UK', 'Canada', 'Germany', 'France', 'Spain', 'Switzerland',
  'Singapore', 'Australia',
]

// ---------------------------------------------------------------------------
// UniversityAutocomplete
// ---------------------------------------------------------------------------

interface UniversityAutocompleteProps {
  value: string
  onCommit: (name: string) => void
}

function UniversityAutocomplete({ value, onCommit }: UniversityAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value)
  const [open, setOpen] = useState(false)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  // Sync if parent value changes externally (e.g. server data loads)
  useEffect(() => {
    setInputValue(value)
  }, [value])

  // Debounce the search query by 300 ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(inputValue.trim()), 300)
    return () => clearTimeout(t)
  }, [inputValue])

  const { data: searchData, isFetching } = useQuery<ApiResponse<ProgramTemplate[]>>({
    queryKey: ['university-search', debouncedQuery],
    queryFn: () =>
      api.get<ApiResponse<ProgramTemplate[]>>(
        `/v1/program-templates/search?q=${encodeURIComponent(debouncedQuery)}`,
      ),
    enabled: debouncedQuery.length >= 2,
    staleTime: 60_000,
  })

  // Deduplicate university names from results
  const suggestions = useMemo<string[]>(() => {
    const templates = searchData?.data ?? []
    const seen = new Set<string>()
    const names: string[] = []
    for (const t of templates) {
      if (!seen.has(t.universityName)) {
        seen.add(t.universityName)
        names.push(t.universityName)
      }
    }
    return names
  }, [searchData])

  // Close on click-outside
  useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [])

  function handleSelect(name: string) {
    setInputValue(name)
    onCommit(name)
    setOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={inputValue}
          placeholder="Search university..."
          className="w-full rounded-md border bg-background py-2 pl-8 pr-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onChange={(e) => {
            setInputValue(e.target.value)
            setOpen(true)
          }}
          onFocus={() => {
            if (inputValue.trim().length >= 2) setOpen(true)
          }}
          onBlur={() => onCommit(inputValue.trim())}
        />
      </div>

      {open && (inputValue.trim().length >= 2) && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border bg-popover shadow-lg">
          {isFetching && (
            <p className="px-3 py-2 text-xs text-muted-foreground">Searching...</p>
          )}
          {!isFetching && suggestions.length === 0 && debouncedQuery.length >= 2 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">No universities found.</p>
          )}
          <div className="max-h-48 overflow-y-auto">
            {suggestions.map((name) => (
              <button
                key={name}
                type="button"
                onMouseDown={(e) => e.preventDefault()} // keep input focused until click
                onClick={() => handleSelect(name)}
                className={cn(
                  'flex w-full items-center px-3 py-2 text-left text-sm hover:bg-accent',
                  name === inputValue && 'bg-accent/50 font-medium',
                )}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// PostAdmitPage
// ---------------------------------------------------------------------------

/**
 * PostAdmitPage — post-admission checklist with 12 tasks.
 *
 * Country and university selectors at top.
 * Optimistic checkbox updates.
 */
export default function PostAdmitPage() {
  const queryClient = useQueryClient()
  const { currentWorkspaceId } = useWorkspaceStore()

  const { data, isLoading } = useQuery<ApiResponse<PostAdmitData>>({
    queryKey: ['post-admit', currentWorkspaceId],
    queryFn: () => api.get<ApiResponse<PostAdmitData>>('/v1/post-admit'),
    enabled: !!currentWorkspaceId,
  })
  const postAdmit = data?.data

  const [country, setCountry] = useState('')
  const [universityName, setUniversityName] = useState('')
  const [patchError, setPatchError] = useState<string | null>(null)

  const patchMutation = useMutation({
    mutationFn: (patch: Partial<PostAdmitData>) => api.patch('/v1/post-admit', patch),
    onMutate: async (patch) => {
      setPatchError(null)
      await queryClient.cancelQueries({ queryKey: ['post-admit', currentWorkspaceId] })
      const previous = queryClient.getQueryData<ApiResponse<PostAdmitData>>([
        'post-admit', currentWorkspaceId,
      ])
      queryClient.setQueryData<ApiResponse<PostAdmitData>>(
        ['post-admit', currentWorkspaceId],
        (old) => old && old.data ? { ...old, data: { ...old.data, ...patch } } : old,
      )
      return { previous }
    },
    onError: (err, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['post-admit', currentWorkspaceId], context.previous)
      }
      setPatchError(err instanceof Error ? err.message : 'Failed to save. Please try again.')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['post-admit', currentWorkspaceId] })
    },
  })

  const handleCheckbox = useCallback(
    (key: PostAdmitTaskKey, value: boolean) => {
      patchMutation.mutate({ [key]: value })
    },
    [patchMutation],
  )

  const completedCount = TASK_ORDER.filter((k) => postAdmit?.[k] === true).length

  // Sync local state from server on first load
  useEffect(() => {
    if (postAdmit) {
      if (postAdmit.selectedCountry) setCountry(postAdmit.selectedCountry)
      if (postAdmit.selectedUniversityName) setUniversityName(postAdmit.selectedUniversityName)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!postAdmit])

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-semibold text-foreground mb-6">Post-Admit Checklist</h1>

      {/* Country + University selectors */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-muted-foreground">Destination Country</span>
          <select
            value={country}
            onChange={(e) => {
              setCountry(e.target.value)
              patchMutation.mutate({ selectedCountry: e.target.value })
            }}
            className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select country...</option>
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-muted-foreground">University</span>
          <UniversityAutocomplete
            value={universityName}
            onCommit={(name) => {
              if (name === universityName) return
              setUniversityName(name)
              patchMutation.mutate({ selectedUniversityName: name || undefined })
            }}
          />
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Progress</span>
          <span className="text-sm font-semibold text-brand-blue">
            {completedCount} / {TASK_ORDER.length} tasks
          </span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-brand-green transition-all duration-500"
            style={{ width: `${(completedCount / TASK_ORDER.length) * 100}%` }}
          />
        </div>
        {completedCount === TASK_ORDER.length && (
          <p className="mt-2 text-sm font-semibold text-brand-green text-center">
            All tasks complete!
          </p>
        )}
      </div>

      {/* Error banner */}
      {patchError && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <span>{patchError}</span>
          <button type="button" onClick={() => setPatchError(null)} className="ml-4 shrink-0 text-destructive/70 hover:text-destructive">✕</button>
        </div>
      )}

      {/* Task cards */}
      {isLoading && (
        <div className="text-center py-8 text-sm text-muted-foreground">Loading...</div>
      )}

      {!isLoading && (
        <div className="space-y-3">
          {TASK_ORDER.map((key) => {
            const checked = postAdmit?.[key] === true
            const description = postAdmit?.taskContent?.[key] ?? TASK_DEFAULT_DESCRIPTIONS[key]
            return (
              <div
                key={key}
                className={cn(
                  'flex items-start gap-4 rounded-xl border bg-card p-4 transition-colors',
                  checked && 'opacity-70',
                )}
              >
                <button
                  type="button"
                  onClick={() => handleCheckbox(key, !checked)}
                  aria-label={`Mark ${TASK_DISPLAY_NAMES[key]} as ${checked ? 'incomplete' : 'complete'}`}
                  className={cn(
                    'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    checked
                      ? 'border-brand-green bg-brand-green text-white'
                      : 'border-muted-foreground/40 hover:border-brand-green',
                  )}
                >
                  {checked && (
                    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm font-semibold text-foreground', checked && 'line-through')}>
                    {TASK_DISPLAY_NAMES[key]}
                  </p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
